import { SapBooleanEnum } from '@shared-enums/sap.enum';
import { convertSapDateToFormat } from '@shared-helpers/date-helpers';
import { safeCompare, trimDecimalZeros } from '@shared-helpers/string-number-helpers';
import { ISkfConfigEntity } from '@shared-interfaces/api';

import { ICatRecord } from '../../interfaces/incoming';
import { MessageService } from '../../services/message.service';
import { BasicInterface } from '../basicInterface';
import { ProductionGroupQuantity } from './ProductionGroupQuantity';
import { ProductionGroupText } from './ProductionGroupText';

export class ProductionGroup extends BasicInterface {
  public static readonly magicGroupItemPropertyNamePrefix = 'grpItem';
  //******************************************************************************************
  //!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!
  //NOTE: The below public property and all of the `grpItem${i}` are accessed by outside code
  // via STRING INDEX. This is not type safe at all and these properties might appear no not
  // be used anywhere, but this MUST remain public and their names CANNOT CHANGE. Renaming
  // these will break the the app since they are access via string index!
  //public grpItem0, public grpItem1, public grpItem2, etc...
  //******************************************************************************************
  public readableDate: string;

  constructor(
    entities: Array<ISkfConfigEntity>,
    acting: string,
    user: string,
    recOrder: string,
    public ExtDocumentno: string,
    public id: string,
    private readonly date: string,
    private readonly records: ICatRecord[],
    private readonly messageService?: MessageService,
  ) {
    super('billing');
    this.constructValues(entities, acting, user, recOrder, ExtDocumentno, records);
    this.readableDate = convertSapDateToFormat(date, 'ddd MM/DD');
  }

  public filterByTimesheet(extDocNo: string): void {
    if (!extDocNo) {
      this.show();
    } else if (safeCompare(extDocNo, this.ExtDocumentno)) {
      this.show();
    } else {
      this.hide();
    }
  }

  public getRecordsForMoveTimesheet(): Partial<ICatRecord>[] {
    return this.records
      .filter((rec) => rec && !!rec.Counter)
      .map((record) => ({
        Extdocumentno: record.Extdocumentno,
        Counter: record.Counter,
      }));
  }

  public getTextOrQuantityByStringPropertyName(propertyName: string): ProductionGroupQuantity | ProductionGroupText {
    if (!propertyName.startsWith(ProductionGroup.magicGroupItemPropertyNamePrefix)) {
      throw new Error(`The property name '${propertyName}' is not a valid production group header name!`);
    }
    //@ts-expect-error - We have to ignore typescript errors so we can do this weird magic string lookup on this class
    return this[propertyName];
  }

  private constructValues(
    entities: Array<ISkfConfigEntity>,
    acting: string,
    user: string,
    recorder: string,
    extdocumentno: string,
    records: ICatRecord[],
  ): void {
    //eslint-disable-next-line complexity -- TODO: split up this logic to make this less of a complex method
    entities.forEach((x, i) => {
      if (x.Type === 'EQUIPMENT') {
        this.messageService.error(
          'Production groups has not been set up correctly and will not work until the issue is fixed. Please contact Davey Time support',
        );
        console.error(
          `Production Group at index ${i.toString()} has a "Type" of "EQUIPMENT" and is marked as "ProdGroup" in SAP and needs to be fixed for production groups to work properly\n`,
          `Record Information:`,
          x,
        );
      }
      const record = records.find((y) => x.SKF === y.Statkeyfig);
      const material = record?.Zmatnr ?? '';
      const quantity = record?.Quantity ?? '';
      const text = (record?.LongTextstr ?? '') as SapBooleanEnum; //this "text" is actually an SAP boolean string thingy
      const counter = record?.Counter ?? '';

      //TODO: Either find a way to loop over these magic properties or push them into an array for easier accessing, but that needs to work with the nestedObjectName feature!

      //******************************************************************************************
      //Below we dynamically modify the class by directly creating properties without typescript
      // knowing anything about them. This currently must be done because other code outside of
      // this class relies on the specific property names as string representations. They are
      // accessed via string index lookup so the spelling must stay the same. Search the code
      // for 'grpItem' to find all possible usages
      if (x.Type === 'PRODUCTION') {
        //@ts-expect-error - We have to ignore typescript errors so we can do this weird magic string lookup on this class
        this[ProductionGroup.magicGroupItemPropertyNamePrefix + i.toString()] = new ProductionGroupQuantity(
          acting,
          this.date,
          trimDecimalZeros(quantity),
          user,
          recorder,
          material,
          extdocumentno,
          x.SKF,
          this.id,
          counter,
        );
      } else {
        //@ts-expect-error - We have to ignore typescript errors so we can do this weird magic string lookup on this class
        this[ProductionGroup.magicGroupItemPropertyNamePrefix + i.toString()] = new ProductionGroupText(
          acting,
          this.date,
          text,
          user,
          recorder,
          material,
          extdocumentno,
          x.SKF,
          this.id,
          counter,
        );
      }
    });
  }
}
